import type {
  SitePrivateConfig,
  RecordEntityId,
  SitePublicConfig,
  CognitionGroups,
} from '@centric-os/types';
import { defineStore, type StateTree } from 'pinia';
import { cdlApi } from '@centric-os/app/src/api';

/**
 * Interface for the state of the `useSiteConfigStore`.
 */
interface State extends StateTree {
  sitePublicConfig: SitePublicConfig | null;
  sitePrivateConfig: SitePrivateConfig | null;
  siteCognitionIds: CognitionGroups | null;
}

/**
 * Pinia store for managing the site configuration.
 * This store handles fetching and saving public/private site configuration data and cognition groups.
 *
 * @param {string} [storeId='site-configuration'] - The unique ID of the store.
 * @returns {import('pinia').Store} The Pinia store instance for site configuration.
 */
export function useSiteConfigStore(storeId = 'site-configuration') {
  const store = defineStore(storeId, {
    state: (): State => ({
      sitePublicConfig: null,
      sitePrivateConfig: null,
      siteCognitionIds: null,
    }),
    actions: {
      /**
       * Fetches the public configuration for a specific site.
       *
       * @param {RecordEntityId} siteId - The ID of the site to fetch the public configuration for.
       * @returns {Promise<void>} A promise that resolves once the public configuration is fetched and stored in the state.
       */
      async getSitePublicConfig(siteId: RecordEntityId): Promise<void> {
        const data = await cdlApi
          .get<SitePublicConfig>(`/config/public/${siteId}`)
          .then((r) => r.data);
        this.sitePublicConfig = data;
      },

      /**
       * Fetches the private configuration for a specific site.
       *
       * @param {RecordEntityId} siteId - The ID of the site to fetch the private configuration for.
       * @returns {Promise<void>} A promise that resolves once the private configuration is fetched and stored in the state.
       */
      async getSitePrivateConfig(siteId: RecordEntityId): Promise<void> {
        const data = await cdlApi.get<SitePrivateConfig>(`/config/${siteId}`).then((r) => r.data);
        this.sitePrivateConfig = data;
      },

      /**
       * Fetches both public and private configurations for a specific site concurrently.
       * Uses `Promise.all` to fetch both configurations in parallel.
       *
       * @param {RecordEntityId} siteId - The ID of the site to fetch both public and private configurations for.
       * @returns {Promise<void>} A promise that resolves once both public and private configurations are fetched and stored.
       */
      async fetchSiteConfigurations(siteId: RecordEntityId): Promise<void> {
        const publicConfig = this.getSitePublicConfig(siteId);
        const privateConfig = this.getSitePrivateConfig(siteId);
        await Promise.all([publicConfig, privateConfig]);
      },

      /**
       * Fetches cognition groups for all sites.
       * Note: The current API fetches cognition groups globally across sites, not targeting a specific site.
       * This will need to be updated once the API supports site-specific cognition groups.
       *
       * @returns {Promise<void>} A promise that resolves once the cognition groups are fetched and stored in the state.
       */
      async getSitesCognition(): Promise<void> {
        const data = await cdlApi
          .get<CognitionGroups>(`/config/public/cognition`)
          .then((r) => r.data);
        this.siteCognitionIds = data;
      },

      /**
       * Saves the cognition groups for all sites.
       * Note: The current API saves cognition groups globally across sites, not targeting a specific site.
       * This will need to be updated once the API supports site-specific cognition groups.
       *
       * @param {CognitionGroups} payload - The cognition groups to be saved.
       * @returns {Promise<void>} A promise that resolves once the cognition groups are successfully saved.
       */
      async saveSitesCognition(payload: CognitionGroups): Promise<void> {
        const data = await cdlApi
          .post(`config/public/cognition`, {
            ...payload,
          })
          .then((r) => r.data);
        this.siteCognitionIds = data;
      },

      /**
       * Saves the private configuration for a specific site.
       *
       * @param {string} id - The ID of the site whose private configuration is being saved.
       * @param {Partial<SitePrivateConfig>} payload - A partial object representing the updated private configuration.
       * @returns {Promise<void>} A promise that resolves once the private configuration is successfully saved.
       */
      async savePrivate(id: string, payload: Partial<SitePrivateConfig>): Promise<void> {
        await cdlApi.post(`config/${id}`, {
          ...payload,
        });
      },

      /**
       * Saves the public configuration for a specific site.
       *
       * @param {string} id - The ID of the site whose public configuration is being saved.
       * @param {Partial<SitePublicConfig>} payload - A partial object representing the updated public configuration.
       * @returns {Promise<void>} A promise that resolves once the public configuration is successfully saved.
       */
      async savePublic(id: string, payload: Partial<SitePublicConfig>): Promise<void> {
        await cdlApi.post(`config/public/${id}`, {
          ...payload,
        });
      },
    },
    pagination: false,
  });

  return store();
}
